/**
 * GraphQL Queries and Mutations for Configuration
 * Mirrors the REST API functions from configApi.js
 * Uses the existing clientQuery/clientMutation pattern for proper auth handling
 */

import { clientQuery, clientMutation } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';

/**
 * Normalize form field property names from GraphQL schema to internal format
 * GraphQL uses: name, dataType, hint
 * Internal uses: fieldName, fieldType, hint
 */
const normalizeFormField = (field) => {
	if (!field) return field;

	// Get the field name - prefer non-empty values
	// Check for truthy values (not empty string, null, or undefined)
	const fieldName = (field.fieldName && field.fieldName.trim()) || field.name || '';
	const fieldType = field.fieldType || field.dataType || 'text';

	return {
		...field,
		// Map GraphQL property names to internal names
		fieldName,
		fieldType,
		// Keep fieldId if present, or generate one based on name for stable matching
		fieldId: field.fieldId || field.id || `field-${fieldName}`,
	};
};

/**
 * Normalize inspection details to ensure consistent field property names
 */
const normalizeInspectionDetails = (inspectionDetails) => {
	if (!inspectionDetails) return [];
	return inspectionDetails.map((inspection) => ({
		...inspection,
		formData: (inspection.formData || []).map(normalizeFormField),
	}));
};

/**
 * Strip ALL leading [ and trailing ] brackets from a string
 * Fixes bug where GraphQL String type causes brackets to accumulate
 * e.g., "[[utility]]" -> "utility"
 */
const stripBrackets = (str) => {
	if (typeof str !== 'string') return str;
	return str.replace(/^\[+|\]+$/g, '');
};

/**
 * Normalize assetType to a clean array without accumulated brackets
 * Handles both array and string inputs (GraphQL returns String, frontend uses array)
 */
const normalizeAssetType = (assetType) => {
	if (!assetType) return [];
	if (Array.isArray(assetType)) {
		// Strip brackets from each element in case they accumulated
		return assetType.map(s => stripBrackets(s)).filter(Boolean);
	}
	if (typeof assetType === 'string') {
		// Strip brackets first, then split by comma
		const cleaned = stripBrackets(assetType);
		return cleaned.split(',').map(s => s.trim()).filter(Boolean);
	}
	return [];
};

/**
 * Ensure a value is a string without double-quoting
 * Fixes bug where JSON.stringify was called on values that are already strings,
 * resulting in double-quoted values like "\"1000000000\"" instead of "1000000000"
 */
const ensureStringValue = (value) => {
	if (value == null) return null;
	if (typeof value === 'string') return value;
	return JSON.stringify(value);
};

/**
 * Convert assetType array to comma-separated string for GraphQL API
 * The GraphQL schema expects assetType as String!, not an array
 */
const assetTypeToString = (assetType) => {
	if (!assetType) return '';
	if (Array.isArray(assetType)) {
		// Strip brackets from each element and join with comma
		return assetType.map(s => stripBrackets(s)).filter(Boolean).join(',');
	}
	if (typeof assetType === 'string') {
		return stripBrackets(assetType);
	}
	return '';
};

/**
 * Normalize zone layouts (asset layouts) when loading from API
 * Strips brackets from assetType to fix accumulated bracket bug
 * Returns assetType as array for frontend use
 */
const normalizeZoneLayouts = (layouts) => {
	if (!layouts) return [];
	return layouts.map(layout => ({
		...layout,
		assetType: normalizeAssetType(layout.assetType),
	}));
};

/**
 * Get pending configuration for a company
 * @param {number} companyId - Company ID (integer)
 * @returns {Promise<Object>} Pending configuration with parsed data fields
 */
export const getPendingConfig = async (companyId) => {
	if (!companyId) {
		return null;
	}

	const query = `
		query GetPendingConfig($companyId: Int!) {
			getPendingConfig(companyId: $companyId) {
				version
				configType
				data {
					draft
					pending
					inspectionDetails {
						description
						detailCategory
						detailType
						categoryId
						typeId
						legacyId
						enforceOrder
						legallyMandated
						prepopulateDefects
						requiresReview
						zoneless
						associateDriver
						formData {
							dataType
							hint
							name
							required
							category
							default
							maximum
							maxDecimal
							minimum
							regex
							selects
						}
					}
					assetLayouts {
						assetType
						layoutName
						layoutId
						legacyId
						assetViewGrid
						assetViewId
						components {
							component
							maxSeverity
							minSeverity
							conditions
						}
						configZones {
							tag
							tagNumber
							tagType
							tagComponents
							inspectionTypes
							assetViewLocation
						}
					}
				}
				changelog
				createdOn
				updatedOn
			}
		}
	`;

	let data;
	try {
		data = await clientQuery(
			query,
			{ companyId },
			endpoints.GRAPHQL_GENERAL_V2
		);
	} catch (err) {
		// Return null instead of throwing - no pending config is a valid state
		return null;
	}

	// Handle null response from GraphQL
	if (!data || !data.getPendingConfig) {
		return null;
	}

	const config = data.getPendingConfig;
	if (!config) return null;

	// ConfigData is now a structured object, not AWSJSON
	const configData = config.data || {};

	// Return in the same format expected by the slice
	// Note: GraphQL uses assetLayouts, but slice uses zoneLayouts
	return {
		version: config.version,
		configType: config.configType || 'evir',
		pending: configData.pending !== false,
		zoneLayouts: normalizeZoneLayouts(configData.assetLayouts), // Normalize assetType brackets
		inspectionDetails: normalizeInspectionDetails(configData.inspectionDetails),
		created: config.createdOn,
		updated: config.updatedOn,
	};
};

/**
 * Get active (deployed/published) configuration for a company
 * @param {Object} options - Query options
 * @param {string} options.id - Optional config ID
 * @param {boolean} options.draft - Whether to get draft (default: false)
 * @param {boolean} options.legacy - Whether to get legacy format (default: false)
 * @returns {Promise<Object>} Active configuration with parsed data fields
 */
export const getActiveConfig = async (options = {}) => {
	console.log('[getActiveConfig] Called with options:', options);
	const query = `
		query GetPublishedConfig($id: ID, $draft: Boolean, $legacy: Boolean) {
			getPublishedConfig(id: $id, draft: $draft, legacy: $legacy) {
				version
				configType
				data {
					draft
					pending
					inspectionDetails {
						description
						detailCategory
						detailType
						categoryId
						typeId
						legacyId
						enforceOrder
						legallyMandated
						prepopulateDefects
						requiresReview
						zoneless
						associateDriver
						formData {
							dataType
							hint
							name
							required
							category
							default
							maximum
							maxDecimal
							minimum
							regex
							selects
						}
					}
					assetLayouts {
						assetType
						layoutName
						layoutId
						legacyId
						assetViewGrid
						assetViewId
						components {
							component
							maxSeverity
							minSeverity
							conditions
						}
						configZones {
							tag
							tagNumber
							tagType
							tagComponents
							inspectionTypes
							assetViewLocation
						}
					}
				}
				createdOn
				updatedOn
			}
		}
	`;

	let data;
	try {
		data = await clientQuery(
			query,
			{
				id: options.id || null,
				draft: options.draft || false,
				legacy: options.legacy || false,
			},
			endpoints.GRAPHQL_GENERAL_V2
		);
		console.log('[getActiveConfig] GraphQL response:', data ? 'received' : 'null');
	} catch (err) {
		console.error('[getActiveConfig] GraphQL error:', err);
		return null;
	}

	// Handle null response from GraphQL
	if (!data || !data.getPublishedConfig) {
		console.log('[getActiveConfig] No published config found, returning null');
		return null;
	}
	console.log('[getActiveConfig] Found published config with zoneLayouts:', data.getPublishedConfig?.data?.assetLayouts?.length);

	const config = data.getPublishedConfig;
	if (!config) return null;

	// ConfigData is now a structured object, not AWSJSON
	const configData = config.data || {};

	// Return in the same format expected by the slice
	// Note: GraphQL uses assetLayouts, but slice uses zoneLayouts
	return {
		version: config.version,
		configType: config.configType || 'evir',
		pending: false, // Published config is never pending
		zoneLayouts: normalizeZoneLayouts(configData.assetLayouts), // Normalize assetType brackets
		inspectionDetails: normalizeInspectionDetails(configData.inspectionDetails),
		created: config.createdOn,
		updated: config.updatedOn,
	};
};

/**
 * Get all published configs for a company
 * companyId is inferred by the backend from the user's auth token
 * @returns {Promise<Array>} List of published configurations
 */
export const getPublishedConfigs = async () => {
	const query = `
		query SearchPublishedConfigs {
			searchPublishedConfigs(limit: 100) {
				items {
					id
					sequence
					version
					configType
					data
					createdOn
					updatedOn
				}
				total
			}
		}
	`;

	const data = await clientQuery(
		query,
		{},
		endpoints.GRAPHQL_GENERAL_V2
	);

	if (!data || !data.searchPublishedConfigs || !data.searchPublishedConfigs.items) {
		return [];
	}

	return data.searchPublishedConfigs.items;
};

/**
 * Save configuration (draft mode)
 * Accepts the same payload format as the REST API and packs it into the GraphQL data field.
 * @param {Object} configPayload - Configuration payload (REST API format)
 * @param {Object|boolean} options - Options object or boolean for backward compatibility
 * @param {boolean} options.deploy - Whether to deploy (default: false)
 * @param {boolean} options.pending - Whether this is a pending config (default: true, set to false to clear pending state)
 * @returns {Promise<Object>} Response data
 */
export const saveConfiguration = async (configPayload, options = {}) => {
	// Backward compatibility: if options is a boolean, treat it as deploy flag
	const normalizedOptions = typeof options === 'boolean' ? { deploy: options } : options;
	const { deploy = false, pending = true } = normalizedOptions;

	if (deploy) {
		return deployConfiguration(configPayload);
	}

	const mutation = `
		mutation CreatePendingConfig(
			$version: String!
			$configType: String!
			$data: ConfigDataInput!
			$changelog: AWSJSON
		) {
			createPendingConfig(
				version: $version
				configType: $configType
				data: $data
				changelog: $changelog
			) {
				version
				configType
				data {
					draft
					pending
					inspectionDetails {
						typeId
						detailType
						description
					}
					assetLayouts {
						layoutId
						layoutName
						assetType
					}
				}
				changelog
				createdOn
				updatedOn
			}
		}
	`;

	// Helper to validate UUID format (categoryId, typeId must be valid UUIDs or null)
	const isValidUUID = (str) => {
		if (!str) return false;
		const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
		return uuidRegex.test(str);
	};

	// EVIRPX-659: Build a lookup of valid categoryIds by detailCategory
	// This allows us to find a fallback categoryId for inspections with invalid ones
	const categoryIdLookup = {};
	let fallbackCategoryId = null;
	(configPayload.inspectionDetails || []).forEach(insp => {
		if (isValidUUID(insp.categoryId)) {
			if (!fallbackCategoryId) fallbackCategoryId = insp.categoryId;
			if (insp.detailCategory && !categoryIdLookup[insp.detailCategory]) {
				categoryIdLookup[insp.detailCategory] = insp.categoryId;
			}
		}
	});

	// Transform to ConfigDataInput format (matching GraphQL schema)
	const dataInput = {
		draft: false,
		pending: pending,
		inspectionDetails: (configPayload.inspectionDetails || []).map((insp, index) => {
			// EVIRPX-659: Backend requires valid UUID for categoryId
			// If invalid, try to find a matching categoryId from another inspection with same detailCategory
			// If still not found, use any valid categoryId as fallback
			let validCategoryId = insp.categoryId;
			if (!isValidUUID(validCategoryId)) {
				validCategoryId = categoryIdLookup[insp.detailCategory] || fallbackCategoryId;
			}
			const validTypeId = isValidUUID(insp.typeId) ? insp.typeId : undefined;
			return {
				description: insp.description || '',
				detailCategory: insp.detailCategory || 'other',
				detailType: insp.detailType || '',
				enforceOrder: insp.enforceOrder || false,
				legallyMandated: insp.legallyMandated || false,
				prepopulateDefects: insp.prepopulateDefects || false,
				requiresReview: insp.requiresReview || false,
				zoneless: insp.zoneless || false,
				associateDriver: insp.associateDriver || false,
				// EVIRPX-659: Ensure categoryId and typeId are valid UUIDs or omitted
				categoryId: validCategoryId,
				typeId: validTypeId,
				legacyId: insp.legacyId || null,
				formData: (insp.formData || []).map(field => ({
					dataType: field.fieldType || field.dataType || 'text',
					hint: field.hint || '',
					name: field.name || field.fieldName || '',
					required: field.required || false,
					category: field.category || null,
					default: ensureStringValue(field.default),
					maximum: ensureStringValue(field.maximum),
					minimum: ensureStringValue(field.minimum),
					maxDecimal: field.maxDecimal || null,
					regex: field.regex || null,
					selects: field.selects || null,
				})),
			};
		}),
		assetLayouts: (configPayload.zoneLayouts || []).map(layout => {
			// EVIRPX-659: Ensure assetViewId and assetViewGrid are consistent
			// Backend requires both to have values or both to be null
			const assetViewId = layout.assetViewId || null;
			const assetViewGrid = assetViewId ? (layout.assetViewGrid || null) : null;

			return {
				assetType: assetTypeToString(layout.assetType),
				layoutName: layout.layoutName || '',
				layoutId: layout.layoutId || null,
				legacyId: layout.legacyId || null,
				assetViewId,
				assetViewGrid,
				components: (layout.components || []).map(comp => ({
					component: comp.component || '',
					maxSeverity: comp.maxSeverity ?? 255,
					minSeverity: comp.minSeverity ?? 0,
					conditions: comp.conditions || [],
				})),
				// EVIRPX-632: assetViewLocation must be null when assetViewId is null
				configZones: (layout.configZones || []).map(zone => ({
					tag: zone.tag || '',
					tagNumber: zone.tagNumber || 0,
					tagType: zone.tagType || 'Zone',
					tagComponents: zone.tagComponents || [],
					inspectionTypes: zone.inspectionTypes || [],
					assetViewLocation: assetViewId ? (zone.assetViewLocation || null) : null,
				})),
			};
		}),
	};

	// Debug: log what we're sending
	console.log('[saveConfiguration] Sending assetLayouts count:', dataInput.assetLayouts?.length);
	console.log('[saveConfiguration] Layout names:', dataInput.assetLayouts?.map(l => l.layoutName));

	const data = await clientMutation(
		mutation,
		{
			version: configPayload.version || '1.0',
			configType: configPayload.configType || 'evir',
			data: dataInput,
			changelog: configPayload.changelog ? JSON.stringify(configPayload.changelog) : null,
		},
		endpoints.GRAPHQL_GENERAL_V2
	);

	// Debug: log what we got back
	console.log('[saveConfiguration] Response:', data);
	console.log('[saveConfiguration] Saved assetLayouts count:', data?.createPendingConfig?.data?.assetLayouts?.length);

	// Validate that the save actually worked
	if (!data || !data.createPendingConfig) {
		throw new Error('Save failed: No response from server');
	}

	return data.createPendingConfig;
};

/**
 * Update configuration by ID
 * @param {string} configId - The config ID (required for deploy)
 * @param {Object} configPayload - Configuration payload
 * @param {boolean} deploy - Whether to deploy (default: true)
 * @returns {Promise<Object>} Response data
 */
export const updateConfiguration = async (configId, configPayload, deploy = true) => {
	if (!configId) {
		throw new Error('Config ID is required. Save the config first.');
	}

	const mutation = `
		mutation UpdatePendingConfig(
			$version: String
			$configType: String
			$data: ConfigDataInput
			$changelog: AWSJSON
		) {
			updatePendingConfig(
				version: $version
				configType: $configType
				data: $data
				changelog: $changelog
			) {
				version
				configType
				data {
					draft
					pending
					inspectionDetails {
						typeId
						detailType
						description
					}
					assetLayouts {
						layoutId
						layoutName
						assetType
					}
				}
				changelog
				updatedOn
			}
		}
	`;

	// Helper to validate UUID format (categoryId, typeId must be valid UUIDs or null)
	const isValidUUID = (str) => {
		if (!str) return false;
		const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
		return uuidRegex.test(str);
	};

	// EVIRPX-659: Build a lookup of valid categoryIds by detailCategory
	const categoryIdLookup = {};
	let fallbackCategoryId = null;
	(configPayload.inspectionDetails || []).forEach(insp => {
		if (isValidUUID(insp.categoryId)) {
			if (!fallbackCategoryId) fallbackCategoryId = insp.categoryId;
			if (insp.detailCategory && !categoryIdLookup[insp.detailCategory]) {
				categoryIdLookup[insp.detailCategory] = insp.categoryId;
			}
		}
	});

	// Transform to ConfigDataInput format (matching GraphQL schema)
	const dataInput = {
		draft: false,
		pending: true,
		inspectionDetails: (configPayload.inspectionDetails || []).map((insp, index) => {
			// EVIRPX-659: Backend requires valid UUID for categoryId
			let validCategoryId = insp.categoryId;
			if (!isValidUUID(validCategoryId)) {
				validCategoryId = categoryIdLookup[insp.detailCategory] || fallbackCategoryId;
			}
			const validTypeId = isValidUUID(insp.typeId) ? insp.typeId : undefined;
			return {
				description: insp.description || '',
				detailCategory: insp.detailCategory || 'other',
				detailType: insp.detailType || '',
				enforceOrder: insp.enforceOrder || false,
				legallyMandated: insp.legallyMandated || false,
				prepopulateDefects: insp.prepopulateDefects || false,
				requiresReview: insp.requiresReview || false,
				zoneless: insp.zoneless || false,
				associateDriver: insp.associateDriver || false,
				// EVIRPX-659: Ensure categoryId and typeId are valid UUIDs
				categoryId: validCategoryId,
				typeId: validTypeId,
				legacyId: insp.legacyId || null,
				formData: (insp.formData || []).map(field => ({
					dataType: field.fieldType || field.dataType || 'text',
					hint: field.hint || '',
					name: field.name || field.fieldName || '',
					required: field.required || false,
					category: field.category || null,
					default: ensureStringValue(field.default),
					maximum: ensureStringValue(field.maximum),
					minimum: ensureStringValue(field.minimum),
					maxDecimal: field.maxDecimal || null,
					regex: field.regex || null,
					selects: field.selects || null,
				})),
			};
		}),
		assetLayouts: (configPayload.zoneLayouts || []).map(layout => {
			// EVIRPX-659: Ensure assetViewId and assetViewGrid are consistent
			// Backend requires both to have values or both to be null
			const assetViewId = layout.assetViewId || null;
			const assetViewGrid = assetViewId ? (layout.assetViewGrid || null) : null;

			return {
				assetType: assetTypeToString(layout.assetType),
				layoutName: layout.layoutName || '',
				layoutId: layout.layoutId || null,
				legacyId: layout.legacyId || null,
				assetViewId,
				assetViewGrid,
				components: (layout.components || []).map(comp => ({
					component: comp.component || '',
					maxSeverity: comp.maxSeverity ?? 255,
					minSeverity: comp.minSeverity ?? 0,
					conditions: comp.conditions || [],
				})),
				// EVIRPX-632: assetViewLocation must be null when assetViewId is null
				configZones: (layout.configZones || []).map(zone => ({
					tag: zone.tag || '',
					tagNumber: zone.tagNumber || 0,
					tagType: zone.tagType || 'Zone',
					tagComponents: zone.tagComponents || [],
					inspectionTypes: zone.inspectionTypes || [],
					assetViewLocation: assetViewId ? (zone.assetViewLocation || null) : null,
				})),
			};
		}),
	};

	const data = await clientMutation(
		mutation,
		{
			version: configPayload.version,
			configType: configPayload.configType || 'evir',
			data: dataInput,
			changelog: configPayload.changelog ? JSON.stringify(configPayload.changelog) : null,
		},
		endpoints.GRAPHQL_GENERAL_V2
	);

	// If deploy flag is true, publish the config after updating
	if (deploy) {
		return deployConfiguration(configPayload);
	}

	return data.updatePendingConfig;
};

/**
 * Deploy configuration (publish mode)
 * Publishes the pending configuration to PublishedConfig.
 * Accepts the same payload format as the REST API.
 * @param {Object} configPayload - Configuration payload (REST API format)
 * @returns {Promise<Object>} Response data
 */
export const deployConfiguration = async (configPayload) => {
	const mutation = `
		mutation CreatePublishedConfig(
			$version: String!
			$configType: String!
			$data: ConfigDataInput!
		) {
			createPublishedConfig(
				version: $version
				configType: $configType
				data: $data
			) {
				version
				configType
				data {
					draft
					pending
					inspectionDetails {
						typeId
						detailType
						description
					}
					assetLayouts {
						layoutId
						layoutName
						assetType
					}
				}
				createdOn
				updatedOn
			}
		}
	`;

	// Helper to validate UUID format (categoryId, typeId must be valid UUIDs or null)
	// EVIRPX-659: Backend rejects invalid categoryId/typeId values
	const isValidUUID = (str) => {
		if (!str) return false;
		const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
		return uuidRegex.test(str);
	};

	// EVIRPX-659: Build a lookup of valid categoryIds by detailCategory
	const categoryIdLookup = {};
	let fallbackCategoryId = null;
	(configPayload.inspectionDetails || []).forEach(insp => {
		if (isValidUUID(insp.categoryId)) {
			if (!fallbackCategoryId) fallbackCategoryId = insp.categoryId;
			if (insp.detailCategory && !categoryIdLookup[insp.detailCategory]) {
				categoryIdLookup[insp.detailCategory] = insp.categoryId;
			}
		}
	});

	// Transform to ConfigDataInput format for published config
	// Published config uses draft: false, pending: false
	const dataInput = {
		draft: false,
		pending: false,
		inspectionDetails: (configPayload.inspectionDetails || []).map((insp, index) => {
			// EVIRPX-659: Backend requires valid UUID for categoryId
			let validCategoryId = insp.categoryId;
			if (!isValidUUID(validCategoryId)) {
				validCategoryId = categoryIdLookup[insp.detailCategory] || fallbackCategoryId;
			}
			const validTypeId = isValidUUID(insp.typeId) ? insp.typeId : undefined;
			return {
				description: insp.description || '',
				detailCategory: insp.detailCategory || 'other',
				detailType: insp.detailType || '',
				enforceOrder: insp.enforceOrder || false,
				legallyMandated: insp.legallyMandated || false,
				prepopulateDefects: insp.prepopulateDefects || false,
				requiresReview: insp.requiresReview || false,
				zoneless: insp.zoneless || false,
				associateDriver: insp.associateDriver || false,
				// EVIRPX-659: Ensure categoryId and typeId are valid UUIDs
				categoryId: validCategoryId,
				typeId: validTypeId,
				legacyId: insp.legacyId || null,
				formData: (insp.formData || []).map(field => ({
					dataType: field.fieldType || field.dataType || 'text',
					hint: field.hint || '',
					name: field.name || field.fieldName || '',
					required: field.required || false,
					category: field.category || null,
					default: ensureStringValue(field.default),
					maximum: ensureStringValue(field.maximum),
					minimum: ensureStringValue(field.minimum),
					maxDecimal: field.maxDecimal || null,
					regex: field.regex || null,
					selects: field.selects || null,
				})),
			};
		}),
		assetLayouts: (configPayload.zoneLayouts || []).map(layout => {
			// EVIRPX-659: Ensure assetViewId and assetViewGrid are consistent
			// Backend requires both to have values or both to be null
			const assetViewId = layout.assetViewId || null;
			const assetViewGrid = assetViewId ? (layout.assetViewGrid || null) : null;

			return {
				assetType: assetTypeToString(layout.assetType),
				layoutName: layout.layoutName || '',
				layoutId: layout.layoutId || null,
				legacyId: layout.legacyId || null,
				assetViewId,
				assetViewGrid,
				components: (layout.components || []).map(comp => ({
					component: comp.component || '',
					maxSeverity: comp.maxSeverity ?? 255,
					minSeverity: comp.minSeverity ?? 0,
					conditions: comp.conditions || [],
				})),
				// EVIRPX-632: assetViewLocation must be null when assetViewId is null
				configZones: (layout.configZones || []).map(zone => ({
					tag: zone.tag || '',
					tagNumber: zone.tagNumber || 0,
					tagType: zone.tagType || 'Zone',
					tagComponents: zone.tagComponents || [],
					inspectionTypes: zone.inspectionTypes || [],
					assetViewLocation: assetViewId ? (zone.assetViewLocation || null) : null,
				})),
			};
		}),
	};

	const data = await clientMutation(
		mutation,
		{
			version: configPayload.version || '1.0',
			configType: configPayload.configType || 'evir',
			data: dataInput,
		},
		endpoints.GRAPHQL_GENERAL_V2
	);

	return data.createPublishedConfig;
};

/**
 * Get translations for a company
 * Note: Translations may be part of the EVIR settings or a separate endpoint
 * companyId is inferred by the backend from the user's auth token
 * @returns {Promise<Object>} Translations object
 */
export const getTranslations = async () => {
	const query = `
		query GetEvirSetting {
			getEvirSetting {
				data
				changelog
				createdOn
				updatedOn
			}
		}
	`;

	let data;
	try {
		data = await clientQuery(
			query,
			{},
			endpoints.GRAPHQL_GENERAL_V2
		);
	} catch (err) {
		console.error('[getTranslations] GraphQL error:', err.error || err.message || err);
		return {};
	}

	// Handle null response
	if (!data || !data.getEvirSetting) {
		console.warn('[getTranslations] No data returned from GraphQL query');
		return {};
	}

	// Extract translations from settings data
	const settingsData = typeof data.getEvirSetting.data === 'string'
		? JSON.parse(data.getEvirSetting.data)
		: data.getEvirSetting.data || {};

	return settingsData.translations || {};
};

/**
 * Save translations for a company
 * companyId is inferred by the backend from the user's auth token
 * @param {Object} newTranslations - Translations to save
 * @returns {Promise<Object>} Response data
 */
export const saveTranslations = async (newTranslations) => {
	const mutation = `
		mutation UpdateEvirSetting($data: AWSJSON, $changelog: AWSJSON) {
			updateEvirSetting(data: $data, changelog: $changelog) {
				data
				changelog
				updatedOn
			}
		}
	`;

	const data = await clientMutation(
		mutation,
		{
			data: JSON.stringify({
				translations: newTranslations,
			}),
			changelog: JSON.stringify({
				action: 'update_translations',
				timestamp: new Date().toISOString(),
			}),
		},
		endpoints.GRAPHQL_GENERAL_V2
	);

	return data.updateEvirSetting;
};

/**
 * Get asset view image (SVG) by assetViewId
 * companyId is inferred by the backend from the user's auth token
 * @param {string} assetViewId - Asset view ID
 * @returns {Promise<string>} SVG content as string
 */
export const getAssetViewImage = async (assetViewId) => {
	const query = `
		query GetAssetView($id: String!) {
			getAssetView(id: $id) {
				id
				name
				data
			}
		}
	`;

	let data;
	try {
		// Asset views are on the General V2 API, not EVIR API
		data = await clientQuery(
			query,
			{ id: assetViewId },
			endpoints.GRAPHQL_GENERAL_V2
		);
	} catch (err) {
		console.error('[getAssetViewImage] GraphQL error:', err.error || err.message || err);
		return null;
	}

	// Handle null response
	if (!data || !data.getAssetView) {
		console.warn('[getAssetViewImage] No data returned from GraphQL query');
		return null;
	}

	// Extract SVG content from data field
	const assetView = data.getAssetView;

	if (assetView && assetView.data) {
		// The data field comes back with multiple layers of escaping from GraphQL
		let svgContent = assetView.data;

		// Keep unescaping until we get clean SVG (no more backslash-escaped quotes in attributes)
		let maxIterations = 10;
		while (maxIterations-- > 0 && typeof svgContent === 'string') {
			// Check if it looks like clean SVG (width="290" not width=\"290\" or width=\\\"290\\\")
			if (svgContent.includes('<svg') && !svgContent.includes('\\"') && !svgContent.includes('\\\\')) {
				break;
			}

			// Try JSON.parse first
			try {
				const parsed = JSON.parse(svgContent);
				if (typeof parsed === 'string') {
					svgContent = parsed;
					continue;
				}
			} catch {
				// Not valid JSON, try manual unescaping
			}

			// Manual unescape - handle multiple levels
			const before = svgContent;
			svgContent = svgContent
				.replace(/\\\\"/g, '\\"')
				.replace(/\\"/g, '"')
				.replace(/\\\\n/g, '\\n')
				.replace(/\\n/g, '\n')
				.replace(/\\\\\\\\/g, '\\\\')
				.replace(/\\\\/g, '\\');

			// Remove surrounding quotes if present
			if (svgContent.startsWith('"') && svgContent.endsWith('"')) {
				svgContent = svgContent.slice(1, -1);
			}

			// If no change, we're done
			if (svgContent === before) {
				break;
			}
		}

		return svgContent;
	}

	return null;
};

/**
 * Get language dictionary for a company
 * In the GraphQL model, the language dictionary is stored as part of the config data.
 * Tries pending config first, falls back to active/published config if no pending.
 * companyId is inferred by the backend from the user's auth token
 * @returns {Promise<Object>} Language dictionary
 */
export const getLanguageDictionary = async () => {
	console.log('[getLanguageDictionary] Starting');

	// Try pending config first
	const pendingConfig = await getPendingConfig();
	console.log('[getLanguageDictionary] pendingConfig evirLanguageDictionary key count:', Object.keys(pendingConfig?.evirLanguageDictionary || {}).length);

	if (pendingConfig?.evirLanguageDictionary && Object.keys(pendingConfig.evirLanguageDictionary).length > 0) {
		console.log('[getLanguageDictionary] Returning from pendingConfig');
		return pendingConfig.evirLanguageDictionary;
	}

	// Fall back to active/published config if no pending config or empty lang dict
	const activeConfig = await getActiveConfig();
	console.log('[getLanguageDictionary] activeConfig evirLanguageDictionary key count:', Object.keys(activeConfig?.evirLanguageDictionary || {}).length);
	console.log('[getLanguageDictionary] Returning from activeConfig');
	return activeConfig?.evirLanguageDictionary || {};
};

/**
 * Search asset views
 * companyId is inferred by the backend from the user's auth token
 * @returns {Promise<Array>} List of asset views
 */
export const getAssetViews = async () => {
	const query = `
		query SearchAssetViews($conditions: [AssetViewSearchInput]) {
			searchAssetViews(conditions: $conditions, limit: 100) {
				items {
					id
					name
				}
				total
			}
		}
	`;

	// Asset views are on the General V2 API, not EVIR API
	// Note: No conditions - fetch all asset views regardless of active status
	const data = await clientQuery(
		query,
		{
			conditions: [],
		},
		endpoints.GRAPHQL_GENERAL_V2
	);

	// Handle null response from GraphQL
	if (!data || !data.searchAssetViews || !data.searchAssetViews.items) {
		console.warn('[getAssetViews] No data returned from GraphQL query');
		return [];
	}

	return data.searchAssetViews.items;
};

/**
 * Search asset subtypes using searchAssetSubtypes GraphQL query
 * Used for typeahead search in Asset Type filter dropdown
 * companyId is inferred by the backend from the user's auth token
 * @param {string} searchPattern - Search pattern to filter by name
 * @param {number} limit - Maximum number of results (default 50)
 * @returns {Promise<Array>} Array of { id, name } objects
 */
export const searchAssetSubtypes = async (searchPattern = '', limit = 50) => {
	const conditions = [];

	// Add name search condition if pattern provided
	if (searchPattern && searchPattern.trim()) {
		conditions.push({
			fields: ['name'],
			terms: [searchPattern.trim()],
			exact: false,
		});
	}

	const query = `
		query SearchAssetSubtypes($conditions: [AssetSubtypeSearchInput], $sort: [AssetSubtypeSortInput], $limit: Int, $offset: Int) {
			searchAssetSubtypes(conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
				items {
					id
					name
				}
				total
			}
		}
	`;

	const variables = {
		conditions: conditions.length > 0 ? conditions : null,
		sort: [{ field: 'name', order: 'ASC' }],
		limit,
		offset: 0,
	};

	let data;
	try {
		data = await clientQuery(
			query,
			variables,
			endpoints.GRAPHQL_GENERAL_V2
		);
	} catch (err) {
		console.error('[searchAssetSubtypes] GraphQL error:', err.error || err.message || err);
		return [];
	}

	if (!data || !data.searchAssetSubtypes || !data.searchAssetSubtypes.items) {
		console.warn('[searchAssetSubtypes] No data returned from GraphQL query');
		return [];
	}

	return data.searchAssetSubtypes.items.map((subtype) => ({
		id: subtype.id,
		name: subtype.name,
		label: subtype.name,
		value: String(subtype.id),
	}));
};
